/**
 * Felix client (Phase E follow-up — renderer via tunnel).
 *
 * Felix is local-only. To let the renderer RUN from the hosted app, the user
 * exposes their local Felix backend through a tunnel (cloudflared) and pastes
 * that URL here once; it's persisted in localStorage. All Felix calls
 * (save graph, upload, run) go browser→tunnel→local Felix (CORS is open on the
 * Felix backend). Mirrors the SKILL_EXECUTOR_URL tunnel pattern, but
 * browser-direct so the URL can change without a redeploy.
 */

const FELIX_URL_KEY = "nexus_felix_url";

/** Default only in local dev: from the hosted HTTPS site the browser blocks
 *  fetches to http://localhost (mixed content), so there's no sane default —
 *  the user must paste their HTTPS cloudflared tunnel URL. */
export function getFelixUrl(): string {
  const saved = localStorage.getItem(FELIX_URL_KEY);
  if (saved) return saved.replace(/\/+$/, "");
  const local = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  return local ? "http://localhost:8910" : "";
}

export function setFelixUrl(url: string): void {
  const clean = url.trim().replace(/\/+$/, "");
  if (clean) localStorage.setItem(FELIX_URL_KEY, clean);
  else localStorage.removeItem(FELIX_URL_KEY);
}

export interface FelixArtifact { name: string; token: string; bytes: number; url?: string; }

/* ---------- hosted mode: chat-ui → Worker → Felix Lambda + R2 ----------
 * Same-origin calls; the session cookie authenticates them (RBAC: writer). No
 * tunnel, no local Felix — the Worker forwards to the Lambda and inputs/artifacts
 * live in R2. */

/** Stage input files in R2 via the Worker; returns the R2 keys for the input node. */
export async function uploadToNexus(instance: string, files: File[]): Promise<{ name: string; key: string }[]> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  const res = await fetch(`/api/${instance}/felix/upload`, { method: "POST", body: fd });
  if (!res.ok) throw new Error(`upload ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return (await res.json()).files as { name: string; key: string }[];
}

/** Run a graph on the Felix Lambda via the Worker. The Lambda's response is
 *  buffered, so live per-node status comes from polling the run's progress
 *  snapshot (GET /felix/runs/<runId> — the Lambda writes it to R2 after every
 *  node event) while the request is in flight; the buffered events then replay
 *  through the same handlers with full results. Handlers must be idempotent
 *  per node — the poll and the replay both report node status. */
export async function runOnNexus(instance: string, graph: unknown, handlers: FelixRunHandlers): Promise<void> {
  const runId = crypto.randomUUID();
  const relayed: Record<string, string> = {};
  const poll = window.setInterval(async () => {
    try {
      const r = await fetch(`/api/${instance}/felix/runs/${runId}`);
      if (!r.ok) return;
      const p = await r.json() as { nodes?: { id: string; status: string }[] };
      for (const n of p.nodes || []) {
        if (n.status === "pending" || relayed[n.id] === n.status) continue;
        relayed[n.id] = n.status;
        if (n.status === "running") handlers.onNodeStart?.(n.id);
        else if (n.status === "done") handlers.onNodeDone?.(n.id, { nodeId: n.id, status: "done" });
        else if (n.status === "error") handlers.onNodeError?.(n.id, "");
      }
    } catch { /* live view only — the buffered replay is authoritative */ }
  }, 1500);

  try {
    const res = await fetch(`/api/${instance}/felix/run`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ graph, runId }),
    });
    if (!res.ok) throw new Error(`run ${res.status}: ${(await res.text()).slice(0, 200)}`);
    const data = await res.json() as { status?: string; events?: any[] };
    for (const p of (data.events || [])) {
      if (p.type === "node_started") handlers.onNodeStart?.(p.nodeId);
      else if (p.type === "node_done") handlers.onNodeDone?.(p.nodeId, p.result);
      else if (p.type === "node_error") handlers.onNodeError?.(p.nodeId, p.error);
      else if (p.type === "run_done") handlers.onRunDone?.(p.status);
    }
    if (!(data.events || []).some((p) => p.type === "run_done")) handlers.onRunDone?.(data.status || "done");
  } finally {
    window.clearInterval(poll);
  }
}
export interface FelixNodeResult {
  nodeId: string;
  status: "idle" | "running" | "done" | "error" | "skipped";
  summary?: string;
  error?: string;
  artifacts?: FelixArtifact[];
}

export interface FelixRunHandlers {
  onNodeStart?: (nodeId: string) => void;
  onNodeDone?: (nodeId: string, result: FelixNodeResult) => void;
  onNodeError?: (nodeId: string, error: string) => void;
  onRunDone?: (status: string) => void;
}

/** Upload files; returns server-side paths the input node references. */
export async function uploadToFelix(base: string, files: File[]): Promise<{ name: string; path: string }[]> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  const res = await fetch(`${base}/api/upload`, { method: "POST", body: fd });
  if (!res.ok) throw new Error(`Felix upload ${res.status}`);
  return (await res.json()).files as { name: string; path: string }[];
}

/** Save a graph to the local Felix store. Returns the assigned id. */
export async function saveGraphToFelix(base: string, graph: unknown): Promise<string> {
  const res = await fetch(`${base}/api/graphs`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ graph }),
  });
  if (!res.ok) throw new Error(`Felix save ${res.status}`);
  const d = await res.json() as { ok: boolean; id: string };
  if (!d.ok) throw new Error("Felix save rejected");
  return d.id;
}

/**
 * Run a graph on the (tunneled) Felix backend and stream node status.
 * Felix emits `data: {type,...}\n\n` SSE events (no `event:` line; the type is
 * inside the JSON).
 */
export async function runFelixGraph(base: string, graph: unknown, handlers: FelixRunHandlers): Promise<void> {
  const res = await fetch(`${base}/api/run`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ graph }),
  });
  if (!res.ok || !res.body) throw new Error(`Felix run ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop() || "";
    for (const ev of events) {
      const line = ev.split("\n").find(l => l.startsWith("data:"));
      if (!line) continue;
      let p: any;
      try { p = JSON.parse(line.slice(5).trim()); } catch { continue; }
      if (p.type === "node_started") handlers.onNodeStart?.(p.nodeId);
      else if (p.type === "node_done") handlers.onNodeDone?.(p.nodeId, p.result);
      else if (p.type === "node_error") handlers.onNodeError?.(p.nodeId, p.error);
      else if (p.type === "run_done") handlers.onRunDone?.(p.status);
    }
  }
}

/** Build an artifact download URL on the (tunneled) Felix backend. */
export function felixArtifactUrl(base: string, token: string): string {
  return `${base}/api/artifact/${token}`;
}
